// Pure daily-look selection helpers. Extracted from the Deno-hosted
// supabase/functions/caileve-daily-look/index.ts so the classification /
// candidate-derivation / repair / missing-slot logic is unit-testable
// from Vitest and can be reused by the client-side pre-generation gate.
//
// DUPLICATION NOTICE: this file has a sibling at
// supabase/functions/_shared/daily-look-selection.ts (Deno-friendly imports
// from ./engagement-scoring.ts). Any change here MUST be mirrored there.
//
// This module is intentionally pure and I/O-free: no Supabase, no fetch,
// no Date.now, no randomness. All external state is passed in.

import {
  classifyGarmentRole,
  classifyAccessoryRole,
  type GarmentRole,
} from "@/lib/style-matching";

export type ClothingLike = {
  id: string;
  name: string;
  clothing_type?: string | null;
  style_attributes?: unknown;
};

export type AccessoryLike = {
  id: string;
  name: string;
  category?: string | null;
};

export type ClothingCandidates<T extends ClothingLike = ClothingLike> = {
  onePiece: T[];
  baseTop: T[];
  midLayer: T[];
  bottom: T[];
  outerwear: T[];
};

// Single source of truth: classifyGarmentRole runs over every clothing
// row and bucketizes it. No SQL keyword pre-filter — that has caused
// silent gaps (e.g. clothing_type "Top" never fetched).
export function deriveClothingCandidates<T extends ClothingLike>(
  items: T[],
): ClothingCandidates<T> {
  const onePiece: T[] = [];
  const baseTop: T[] = [];
  const midLayer: T[] = [];
  const bottom: T[] = [];
  const outerwear: T[] = [];
  for (const i of items) {
    const role: GarmentRole = classifyGarmentRole(
      i.clothing_type,
      i.name,
      (i.style_attributes ?? null) as never,
    );
    if (role === "one_piece") onePiece.push(i);
    else if (role === "base_top") baseTop.push(i);
    else if (role === "mid_layer") midLayer.push(i);
    else if (role === "bottom") bottom.push(i);
    else if (role === "outerwear") outerwear.push(i);
  }
  return { onePiece, baseTop, midLayer, bottom, outerwear };
}

// Client-side pre-generation gate for the Daily Look. The old gate
// (totalCount >= 2) fires generation for a closet with one bottom and
// one necklace — guaranteeing an incomplete look. This gate requires
// at least one jewelry piece AND a clothing foundation (a one-piece,
// or both a base_top and a bottom).
export type FoundationCoverage = {
  hasJewelry: boolean;
  hasClothingFoundation: boolean;
  canGenerate: boolean;
  missingMessage: string | null;
};

export function evaluateFoundationCoverage(input: {
  clothingItems: ClothingLike[];
  jewelryCount: number;
}): FoundationCoverage {
  const cand = deriveClothingCandidates(input.clothingItems);
  const hasJewelry = input.jewelryCount > 0;
  const hasOnePiece = cand.onePiece.length > 0;
  const hasSeparates = cand.baseTop.length > 0 && cand.bottom.length > 0;
  const hasClothingFoundation = hasOnePiece || hasSeparates;
  const canGenerate = hasJewelry && hasClothingFoundation;

  let missingMessage: string | null = null;
  if (!canGenerate) {
    const missing: string[] = [];
    if (!hasClothingFoundation) {
      if (cand.baseTop.length === 0 && cand.bottom.length === 0) {
        missing.push("a top and a bottom (or a dress)");
      } else if (cand.baseTop.length === 0) {
        missing.push("a top (or a dress)");
      } else if (cand.bottom.length === 0) {
        missing.push("a bottom (or a dress)");
      }
    }
    if (!hasJewelry) missing.push("a jewelry piece");
    if (missing.length > 0) {
      const joined = missing.length === 1
        ? missing[0]
        : missing.join(" and ");
      missingMessage = `Add ${joined} to unlock your first daily look.`;
    }
  }
  return { hasJewelry, hasClothingFoundation, canGenerate, missingMessage };
}

// Base-outfit picker with independent top/bottom picks and the repair
// pattern from src/lib/style-selection.ts (needsTop / needsBottom):
// never discard a garment we did find just because its pair wasn't
// found on the first pass.
//
// pick() is a caller-supplied scorer (weighted / seasonal / etc); this
// module stays pure and doesn't score.
export type BasePickResult<T> = {
  sc: T | null;
  sb: T | null;
  kind: "one_piece" | "separates" | "partial_top_only" | "partial_bottom_only" | "none";
};

export function pickBaseOutfit<T extends ClothingLike>(
  filtered: ClothingCandidates<T>,
  fallback: ClothingCandidates<T>,
  pick: (items: T[]) => T | null,
): BasePickResult<T> {
  const onePiecePool = filtered.onePiece.length > 0 ? filtered.onePiece : fallback.onePiece;
  const onePiece = pick(onePiecePool);
  if (onePiece) return { sc: onePiece, sb: null, kind: "one_piece" };

  // Independent picks — do not gate one on the other.
  const topPool = filtered.baseTop.length > 0 ? filtered.baseTop : fallback.baseTop;
  const bottomPool = filtered.bottom.length > 0 ? filtered.bottom : fallback.bottom;
  const topPick = pick(topPool);
  const bottomPick = pick(bottomPool.filter((b) => !topPick || b.id !== topPick.id));

  if (topPick && bottomPick) return { sc: topPick, sb: bottomPick, kind: "separates" };
  if (topPick && !bottomPick) return { sc: topPick, sb: null, kind: "partial_top_only" };
  if (!topPick && bottomPick) return { sc: bottomPick, sb: null, kind: "partial_bottom_only" };
  return { sc: null, sb: null, kind: "none" };
}

// Missing-slot computation for the saved row. UI reads these off the
// style_requests.missing_slots column to render a warm nudge.
export function computeMissingSlots(picks: {
  hasFoundation: boolean;  // false when we saved a jewelry-only partial
  onePieceOnly: boolean;   // true when a dress/jumpsuit filled the base
  hasBag: boolean;
  hasFootwear: boolean;
  baseTopCandidateCount: number;
  bottomCandidateCount: number;
}): string[] {
  const missing: string[] = [];
  if (!picks.hasFoundation) {
    // No wearable base at all — call out both.
    missing.push("top", "bottom");
  }
  if (!picks.hasBag) missing.push("bag");
  if (!picks.hasFootwear) missing.push("footwear");
  return missing;
}

// UI helper: render slots as a warm, non-shaming line.
export function formatMissingSlotsMessage(slots: string[] | null | undefined): string | null {
  if (!slots || slots.length === 0) return null;
  const label = (s: string) => (s === "footwear" ? "shoes" : s);
  const items = slots.map(label);
  const joined = items.length === 1
    ? items[0]
    : items.length === 2
      ? `${items[0]} and ${items[1]}`
      : items.slice(0, -1).join(", ") + `, and ${items[items.length - 1]}`;
  const article = items.length === 1
    ? (/^[aeiou]/i.test(items[0]) ? "an " : "a ")
    : "";
  return `Add ${article}${joined} and Cailève will complete tomorrow's look.`;
}

// Branch decision for the edge function. pickBaseOutfit returns one of
// five kinds; the handler needs to route three of them (one_piece /
// separates → full "complete look" path; partial_top_only /
// partial_bottom_only → surface the found garment honestly with a
// missing-role slot; none → jewelry-only). Extracted from the Deno
// handler so the decision can be unit tested end-to-end.
export type DailyLookBranch =
  | { kind: "complete" }                                    // one_piece or separates
  | { kind: "partial_with_garment"; missing: "top" | "bottom" } // exactly one of top/bottom found
  | { kind: "jewelry_only" };                               // nothing wearable found

export function decideDailyLookBranch(
  baseKind: BasePickResult<ClothingLike>["kind"],
): DailyLookBranch {
  if (baseKind === "one_piece" || baseKind === "separates") return { kind: "complete" };
  if (baseKind === "partial_top_only") return { kind: "partial_with_garment", missing: "bottom" };
  if (baseKind === "partial_bottom_only") return { kind: "partial_with_garment", missing: "top" };
  return { kind: "jewelry_only" };
}

// Missing-slot list for the partial-with-garment branch. Only the
// actually-missing role — do NOT push both "top" and "bottom" the way
// computeMissingSlots does for the jewelry-only branch, since one of
// the two IS present.
export function partialGarmentMissingSlots(
  missing: "top" | "bottom",
  hasBag: boolean,
  hasFootwear: boolean,
): string[] {
  const slots: string[] = [missing];
  if (!hasBag) slots.push("bag");
  if (!hasFootwear) slots.push("footwear");
  return slots;
}

// Rotating, honest copy for the partial-with-garment branch. Names
// both the jewelry anchor and the one garment that was actually
// found, and calls out the missing role warmly. Sibling to
// partialLookMessage (Deno-side) — kept here for unit testability.
export function partialWithGarmentMessage(
  jewelryNames: string[],
  garmentName: string,
  missing: "top" | "bottom",
  dayOfWeek: number,
): string {
  const lead = jewelryNames.length > 1
    ? jewelryNames.slice(0, -1).join(", ") + " and " + jewelryNames[jewelryNames.length - 1]
    : jewelryNames[0] ?? "today's jewelry";
  const gap = missing === "top" ? "a top" : "a bottom";
  const variants = [
    `Let ${lead} and ${garmentName} lead today — add ${gap} and tomorrow's look will be complete.`,
    `${lead} anchors ${garmentName} today. As ${gap} joins the collection, the look will come fully into focus.`,
    `Today begins with ${lead} and ${garmentName}. ${gap === "a top" ? "A top" : "A bottom"} to pair with it, and Cailève will finish the story tomorrow.`,
  ];
  return variants[Math.abs(dayOfWeek) % variants.length];
}

// Convenience re-export for the client gate.
export { classifyAccessoryRole };